import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileText, Plus, Search, Building2, UserCheck, Wallet, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
let BACKEND_URL = !ENV_BACKEND_URL || ENV_BACKEND_URL === '' ? window.location.origin : ENV_BACKEND_URL;
if (BACKEND_URL.endsWith('/api')) BACKEND_URL = BACKEND_URL.slice(0, -4);
const API = `${BACKEND_URL}/api`;

function formatCLP(amount) {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0
  }).format(amount);
}

export default function SubcontractsPage() {
  const [subcontracts, setSubcontracts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newSub, setNewSub] = useState({
      project_id: '',
      contractor_name: '',
      contractor_rut: '',
      description: '',
      total_amount: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subsRes, projRes] = await Promise.all([
        axios.get(`${API}/construction/subcontracts`),
        axios.get(`${API}/construction/projects`)
      ]);
      setSubcontracts(subsRes.data);
      setProjects(projRes.data);
    } catch (error) {
      toast.error('Error al cargar datos de subcontratos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
      e.preventDefault();
      if (!newSub.project_id) {
          toast.error('Debe seleccionar una obra');
          return;
      }
      try {
          await axios.post(`${API}/construction/subcontracts`, newSub);
          toast.success('Subcontrato registrado correctamente');
          setIsCreateOpen(false);
          setNewSub({ project_id: '', contractor_name: '', contractor_rut: '', description: '', total_amount: 0 });
          fetchData();
      } catch (error) {
          toast.error('Error al registrar subcontrato');
      }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <FileText className="h-8 w-8 text-blue-600" />
            Subcontratos
          </h1>
          <p className="text-muted-foreground">Gestión de contratos externos y estados de pago a contratistas</p>
        </div>
        
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="w-full md:w-auto bg-blue-600 hover:bg-blue-700">
              <Plus className="mr-2 h-4 w-4" /> Nuevo Subcontrato
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Registrar Nuevo Subcontrato</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
                <div className="space-y-2">
                    <label className="text-sm font-medium">Obra / Proyecto</label>
                    <Select onValueChange={(val) => setNewSub({...newSub, project_id: val})}>
                        <SelectTrigger>
                            <SelectValue placeholder="Seleccione una obra" />
                        </SelectTrigger>
                        <SelectContent>
                            {projects.map(p => (
                                <SelectItem key={p.id} value={p.id}>{p.name} ({p.code})</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    <label className="text-sm font-medium">Contratista (Nombre/Razón Social)</label>
                    <Input required value={newSub.contractor_name} onChange={e => setNewSub({...newSub, contractor_name: e.target.value})} placeholder="Ej: Hormigones S.A." />
                </div>
                <div className="space-y-2">
                    <label className="text-sm font-medium">RUT Contratista</label>
                    <Input value={newSub.contractor_rut} onChange={e => setNewSub({...newSub, contractor_rut: e.target.value})} placeholder="76.123.456-K" />
                </div>
                <div className="space-y-2">
                    <label className="text-sm font-medium">Descripción del Servicio</label>
                    <Input value={newSub.description} onChange={e => setNewSub({...newSub, description: e.target.value})} placeholder="Ej: Obra gruesa etapa 1" />
                </div>
                <div className="space-y-2">
                    <label className="text-sm font-medium">Monto Total Contrato</label>
                    <Input type="number" required value={newSub.total_amount} onChange={e => setNewSub({...newSub, total_amount: parseFloat(e.target.value)})} />
                </div>
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">Registrar Contrato</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
          <div className="text-center py-12 animate-pulse">Cargando subcontratos...</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {subcontracts.map(sub => (
              <Card key={sub.id} className="hover:shadow-md transition-shadow cursor-pointer group">
                  <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-4">
                          <div className="flex items-center gap-3">
                              <div className="p-2 bg-blue-50 dark:bg-blue-950/30 rounded-lg group-hover:bg-blue-100 transition-colors">
                                  <Building2 className="h-6 w-6 text-blue-600" />
                              </div>
                              <div>
                                  <h3 className="font-bold text-lg">{sub.contractor_name}</h3>
                                  <p className="text-xs text-muted-foreground">RUT: {sub.contractor_rut || 'No info'}</p>
                              </div>
                          </div>
                          <Badge className={`${
                              sub.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'
                          } border-none font-bold uppercase text-[10px]`}>
                              {sub.status === 'active' ? 'Vigente' : sub.status}
                          </Badge>
                      </div>
                      <p className="text-sm mb-4 text-muted-foreground line-clamp-2">
                          <span className="font-semibold text-blue-600 mr-2 uppercase text-[10px]">Obra ID: {sub.project_id}</span>
                          {sub.description || 'Sin descripción'}
                      </p>
                      
                      <div className="grid grid-cols-2 gap-4 border-t pt-4">
                          <div>
                            <span className="text-[10px] uppercase font-bold text-muted-foreground">Monto Contratado</span>
                            <p className="font-bold font-mono text-blue-600">{formatCLP(sub.total_amount)}</p>
                          </div>
                          <div className="flex flex-col justify-end items-end">
                             <Button variant="ghost" size="sm" className="text-xs group-hover:text-blue-600">
                                Ver Detalles <ArrowRight className="h-3 w-3 ml-1" />
                             </Button>
                          </div>
                      </div>
                  </CardContent>
              </Card>
          ))}
          {subcontracts.length === 0 && (
              <div className="col-span-full py-16 text-center border-2 border-dashed rounded-3xl bg-muted/5 flex flex-col items-center gap-4">
                  <FileText className="h-12 w-12 text-muted-foreground opacity-20" />
                  <div className="max-w-xs mx-auto">
                    <p className="text-muted-foreground font-medium">No se han registrado subcontratos aún.</p>
                    <p className="text-xs text-muted-foreground mt-1 text-balance">Los subcontratos permiten gestionar las valorizaciones y retenciones de sus contratistas externos.</p>
                  </div>
              </div>
          )}
        </div>
      )}
    </div>
  );
}
